import { observer } from 'mobx-react';
import { IUnitValue, IUserDashboardWidgetItemModel, UserDashboardWidgetModel } from 'shared/models';

import { getStatusColor } from '../../../../utilities/getStatusColor';
import { formatValueWithUnits } from 'pages/profile-dashboard/utilities/formatValueWithUnits.ts';
import { useDashboardDict } from 'pages/profile-dashboard/context/useDashboardDict.tsx';

import classnames from 'classnames';
import { UiHint } from 'shared/uikit/UiHint';
import { useIsTruncated } from 'shared/hooks';

import './styles.scss';

type PropsType = {
    items: UserDashboardWidgetModel['items'];
    labels?: UserDashboardWidgetModel['labels'];
    className?: string;
    valueType?: "main" | "detailed";
    stickyHeader?: boolean;
};

type StoreRowProps = {
    item: IUserDashboardWidgetItemModel;
    getStoreName: (storeId: number | string | null | undefined) => string;
    getStatus: (value: IUnitValue | IUnitValue[] | null) => { text: string | number | null; color: string };
    renderValueCell: (value?: IUnitValue | IUnitValue[] | null, hint?: string | { main?: string; detailed?: string }) => React.ReactNode;
};

function pickValue(value?: IUnitValue | IUnitValue[] | null, type: "main" | "detailed" = "main") {
    if (!value) return null;
    if (Array.isArray(value)) {
        const found = value.find(v => v.type === type);
        if (found) return found;
        return value[0] ?? null;
    }
    return value;
}

const StoreRow = observer(({
    item, getStatus, getStoreName, renderValueCell
}: StoreRowProps) => {
    const { ref: storeNameRef, isTruncated } = useIsTruncated<HTMLSpanElement>();
    const status = getStatus(item?.value || null);

    return (
        <tr className="ppd-widget-stores-table__row" key={`${item.store_id}-${item.group_id || 0}`}>
            <td className="ppd-widget-stores-table__cell ppd-widget-stores-table__cell--store">
                <span className="ppd-widget-stores-table__cell-store-text" ref={storeNameRef}>
                    {getStoreName(item.store_id)}
                </span>
                {isTruncated && (
                    <span className="ppd-widget-stores-table__full-store-text">
                        {getStoreName(item.store_id)}
                    </span>
                )}
            </td>
            <td className="ppd-widget-stores-table__cell">
                {renderValueCell(item.plan, item.plan_hint || '')}
            </td>
            <td className="ppd-widget-stores-table__cell">
                {renderValueCell(item.fact, item.fact_hint || '')}
            </td>
            <td className="ppd-widget-stores-table__cell">
                <span className="ppd-widget-stores-table__accent-value" style={{ color: status.color }}>{status.text}</span>
            </td>
        </tr>
    );
});

export const PPDWidgetStoresTable = observer(({
    items = [],
    labels = {},
    className,
    valueType = "main",
    stickyHeader = false,
}: PropsType) => {
    const { stores, units } = useDashboardDict();

    const getStoreName = (storeId: number | string | null | undefined) => {
        if (storeId === null) return "Все бутики";
        return stores?.find(s => s.id === storeId)?.name ?? "-";
    };

    const getStatus = (unitValue: IUnitValue | IUnitValue[] | null) => {
        const val = pickValue(unitValue, valueType);
        const color = getStatusColor(val?.amount, "#15171C");

        return {
            text: formatValueWithUnits(val, units) ? formatValueWithUnits(val, units) : "-",
            color,
        };
    };


    const renderValueCell = (value?: IUnitValue | IUnitValue[] | null, hint?: string | { main?: string; detailed?: string; }) => {
        const val = pickValue(value, valueType);
        let finalHint: string = '';

        if (hint) {
            if (typeof hint === "object") {
                finalHint = hint[valueType] ?? '';
            } else {
                finalHint = Array.isArray(value) ? '' : hint;
            }
        }

        return (
            <span className="ppd-widget-stores-table__accent-value">
                { formatValueWithUnits(val, units) ? formatValueWithUnits(val, units) : "-"}
                { !!finalHint && <UiHint text={finalHint} className="ppd-widget-stores-table__cell-hint" />}
            </span>
        );
    };

    return (
        <div className={classnames("ppd-widget-stores-table", className)}>
            <div className="ppd-widget-stores-table__wrapper">
                <table className="ppd-widget-stores-table__table">
                    <thead className=
                        {classnames("ppd-widget-stores-table__thead",
                            { "ppd-widget-stores-table__thead--sticky": stickyHeader })}
                    >
                        <tr className="ppd-widget-stores-table__thead-row">
                            <th className="ppd-widget-stores-table__head">{labels?.store}</th>
                            <th className="ppd-widget-stores-table__head">{labels?.plan}</th>
                            <th className="ppd-widget-stores-table__head">{labels?.fact}</th>
                            <th className="ppd-widget-stores-table__head">{labels?.status}</th>
                        </tr>
                    </thead>
                    <tbody className="ppd-widget-stores-table__body">
                        {items?.map(item => (
                            <StoreRow
                                key={`${item.store_id}-${item.group_id || 0}`}
                                item={item}
                                getStatus={getStatus}
                                getStoreName={getStoreName}
                                renderValueCell={renderValueCell}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
});
